import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { EmployeeConsultantsService } from '../../../../../services/api/hr/employee-consultants.service';
import { OptionService } from '../../../../../services/api/option.service';
import { AlertInfo } from '../../../../../models/alert-info';
import { SavedDataInfo } from '../../../../../models/form/saved-data-info';
import { InputFieldComponent } from '../../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../../components/form/select-field/select-field.component';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';


@Component({
  selector: 'employee-consultants-residential-info-form',
  imports: [ReactiveFormsModule, NgbAlert, InputFieldComponent, SelectFieldComponent],
  templateUrl: './employee-consultants-residential-info-form.component.html',
  styleUrl: './employee-consultants-residential-info-form.component.scss'
})
export class EmployeeConsultantsResidentialInfoFormComponent implements OnInit {
  @Input() employee: any;
  @Input() readonly: boolean = false;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  form!: FormGroup;
  alertInfo: AlertInfo = { show: false, type: 'success', message: '' };
  countries: any[] = [];
  loading: boolean = false;
  validationErrors: any[] = [];

  constructor(
    private fb: FormBuilder,
    private employeeConsultantsService: EmployeeConsultantsService,
    private optionService: OptionService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.optionService.countries().subscribe({
      next: (response: any) => {
        if (response?.data?.options) {
          this.countries = response.data.options.map((country: any) => ({
            key: country.key,
            value: country.value
          }));
          
          if (this.employee) {
            this.updateFormWithEmployeeData();
          }
        }
      },
      error: (error: any) => {
        console.error('Error loading countries:', error);
      }
    });
  }

  initForm(): void {
    this.form = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      birthdate: ['', Validators.required],
      addressLine1: ['', Validators.required],
      addressLine2: [''],
      zipCode: ['', Validators.required],
      city: ['', Validators.required],
      countryId: [null, Validators.required]
    });

    if (this.readonly) {
      this.form.disable();
    }
  }

  private updateFormWithEmployeeData(): void {
    if (this.employee) {
      this.form.patchValue({
        firstName: this.employee?.firstName || '',
        lastName: this.employee?.lastName || '',
        birthdate: this.employee?.birthdate || '',
        addressLine1: this.employee?.addressLine1 || '',
        addressLine2: this.employee?.addressLine2 || '',
        zipCode: this.employee?.zipCode || '',
        city: this.employee?.city || '',
        countryId: this.employee?.countryId || null
      });
    }
  }

  showAlertInfo(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }

  updateValidationErrors(errors: any[]): void {
    this.validationErrors = errors;
    errors.forEach(error => {
      const control = this.form.get(error.field);
      if (control) {
        control.setErrors({ serverError: error.message });
      }
    });
  }


  validateForm(): boolean {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.get(key);
        control?.markAsTouched();
      });
      this.showAlertInfo('danger', 'Please fill in all required fields correctly.');
      return false;
    }
    return true;
  }

 
  saveForm() {
    this.loading = true;
    const employeeData = { ...this.form.value };

    if (this.employee?.employeeId) {
      return this.employeeConsultantsService.update(this.employee.employeeId, employeeData);
    } else {
      return this.employeeConsultantsService.create(employeeData);
    }
  }

 
  onSubmit(): void {
    if (this.loading) return;

    if (!this.validateForm()) return;

    this.saveForm().subscribe({
      next: (response: any) => {
        this.loading = false;
        this.showAlertInfo('success', 'Employee residential information updated successfully');
        this.onSavedData.emit({ success: true, response });
      },
      error: (error: any) => {
        this.loading = false;
        this.showAlertInfo('danger', error.error?.message || 'An error occurred while creating employee');
        this.onSavedData.emit({ success: false, error });
      }
    });
  }

  getFirstNameControl(): FormControl { return this.getFormControl('firstName'); }
  getLastNameControl(): FormControl { return this.getFormControl('lastName'); }
  getBirthdateControl(): FormControl { return this.getFormControl('birthdate'); }
  getAddressLine1Control(): FormControl { return this.getFormControl('addressLine1'); }
  getAddressLine2Control(): FormControl { return this.getFormControl('addressLine2'); }
  getZipCodeControl(): FormControl { return this.getFormControl('zipCode'); }
  getCityControl(): FormControl { return this.getFormControl('city'); }
  getCountryIdControl(): FormControl { return this.getFormControl('countryId'); }

  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }
}
